/**
 * Locale-aware formatters for the console — thin Intl wrappers keyed on
 * the active Lingui locale (callers pass ``i18n.locale``).
 */

// Date/time formatters moved to `@/lib/datetime` once the chat meta row
// needed the same relative label. Re-exported so the console keeps a
// single import surface.
export { formatDate, formatDateTime, formatRelativeTime } from "@/lib/datetime";

export function formatNumber(locale: string, value: number): string {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(locale).format(value);
}

export function formatCompactNumber(locale: string, value: number): string {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * A ratio (``0.58`` → ``58%``) as a locale-aware percentage. ``signed``
 * prefixes a ``+``/``−`` (for period-over-period deltas); non-finite input
 * (e.g. a divide-by-zero baseline) renders an em dash.
 */
export function formatPercent(
  locale: string,
  ratio: number,
  options?: { signed?: boolean; maximumFractionDigits?: number },
): string {
  if (!Number.isFinite(ratio)) return "—";
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
    signDisplay: options?.signed ? "exceptZero" : "auto",
  }).format(ratio);
}

/**
 * A per-1M-token price (USD) for the model catalog — dev-facing platform
 * pricing straight from the API's ``input_usd_per_1m`` / ``output_usd_per_1m``.
 * Returns ``null`` when there is no token cost (``null``/``0`` price, e.g. the
 * Jina retrieval models) so the caller can fall back to the cost-band label.
 */
export function formatPricePerMillion(
  locale: string,
  usd: number | null | undefined,
): string | null {
  if (usd === null || usd === undefined || !Number.isFinite(usd) || usd <= 0) return null;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: usd < 1 ? 2 : 2,
  }).format(usd);
}

/**
 * A whole number of USD cents as a locale-aware USD amount — the unit the
 * platform meters spend in (``X-Spend-*`` headers, the per-key
 * ``monthly_spend_limit_cents`` cap). ``5000`` → ``$50.00``. Non-finite /
 * negative input renders an em dash. Two fraction digits so a $0.50 cap is
 * never rounded away.
 */
export function formatUsdCents(locale: string, cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents) || cents < 0) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * A USD amount in DOLLARS (not cents) — the developer console's estimated,
 * marked-up LLM spend from ``GET /usage`` (``cost_usd``). Sub-$100 figures
 * keep cent precision; larger ones round to whole dollars. Non-finite /
 * negative input renders an em dash. DEVELOPER-facing technical metering —
 * never the customer-facing "% only" surface.
 */
export function formatUsd(locale: string, usd: number | null | undefined): string {
  if (usd === null || usd === undefined || !Number.isFinite(usd) || usd < 0) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: usd < 100 ? 2 : 0,
  }).format(usd);
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

export function formatBytes(locale: string, bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  let value = bytes;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < BYTE_UNITS.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: value >= 100 ? 0 : 1,
  }).format(value);
  return `${formatted} ${BYTE_UNITS[unitIdx] ?? "B"}`;
}

export function formatShortDay(locale: string, isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(date);
}

/**
 * A run/step duration in milliseconds → a compact chip: sub-second stays whole
 * ms (``840ms``), ``≥1s`` switches to one-decimal seconds (``1.2s``); ``null``
 * renders an em dash. Intentionally LOCALE-INDEPENDENT (a terse dev metric, not
 * a localized number) — the console's shared formatter for sync-run / agentic
 * step durations. (``FlowsPage`` deliberately keeps a separate LOCALE-AWARE
 * ``formatDurationMs(locale, ms)`` for its averaged stat tile: different output
 * by design, so it is not consolidated here.)
 */
export function formatDurationMs(ms: number | null): string {
  if (ms == null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}
